import {Loader, Stack} from '@mantine/core'
import {useQuery} from '@tanstack/react-query'
import {createFileRoute} from '@tanstack/react-router'

import {getProspectsByCapacity} from 'src/api/client/api'
import {ManagerFilter} from 'src/components/analytics/manager-filter'
import ProspectDistribution, {
    ProspectDistributionExportContent,
} from 'src/components/analytics/prospect-distribution'
import {useProspectNavigation} from 'src/components/analytics/use-prospect-navigation'
import {ExportImageButton} from 'src/components/export-image-button'
import {PageHeader} from 'src/components/page-header'

type SearchParams = {manager?: string}

export const Route = createFileRoute('/_authed/organizations/$orgId/prospects-by-capacity')({
    component: ProspectsByCapacityPage,
    validateSearch: (search: Record<string, unknown>): SearchParams => ({
        manager: (search['manager'] as string) || undefined,
    }),
})

function ProspectsByCapacityPage() {
    const {orgId} = Route.useParams()
    const {manager} = Route.useSearch()
    const navigateToProspects = useProspectNavigation(orgId)

    const {data, isLoading, error} = useQuery({
        queryKey: ['page', orgId, 'prospects-by-capacity', manager],
        queryFn: () => getProspectsByCapacity(orgId, manager),
    })

    if (isLoading) {
        return <Loader size="lg" style={{display: 'block', margin: '60px auto'}} />
    }

    if (error) {
        return <div>Error loading page: {(error as Error).message}</div>
    }

    if (!data) return null

    const headerTitle = 'Prospects by Capacity'
    const headerDescription = 'Distribution of prospects across estimated giving capacity levels.'

    return (
        <Stack>
            <PageHeader
                title={headerTitle}
                description={headerDescription}
                actions={
                    <ExportImageButton
                        title={headerTitle}
                        exportWidthPx={{table: 420}}
                        renderContent={({kind}) => (
                            <ProspectDistributionExportContent data={data} kind={kind} />
                        )}
                        filenameBase="prospects-by-capacity"
                    />
                }
            />
            <ManagerFilter orgId={orgId} />
            <ProspectDistribution
                data={data}
                onProspectFilter={(filters) => navigateToProspects({...filters, manager})}
            />
        </Stack>
    )
}
